import { NewUserData } from "@/data";
import api from "@/services/api";

export interface PatientInvitationPrefill {
  name: string;
  surname: string;
  email: string;
  phone: string;
}

const getPatientInvitationPrefill = async (invitationToken: string) => {
  const resp = await api.get<{ data: PatientInvitationPrefill }>(
    `/patient-invitations/${invitationToken}`,
  );
  return resp.data.data;
};

const login = async (
    email: string,
    password: string,
    keepUserLoggedIn: boolean,
) => {
    const resp = await api.post("/login", {email, password});
    api.setAccessToken(resp.data.access_token, keepUserLoggedIn);
}

const logout = async () => {
    const resp = await api.post("/logout");
    api.clearAccessToken();
    return resp;
}

const register = async (newUser: NewUserData) => {
    const resp = await api.post("/register", newUser);
    api.setAccessToken(resp.data.access_token, true);
} 

const getUser = async () => {
    return await api.get("/user");
}

const resendEmailVerification = async () => {
    // 204 on success or already verified, 401 if unauthenticated
    return api.post("/email/resend");
}

const markQuestionnaireSubmit = async () => {
    return await api.put("/user", {type_form_submitted: true});
}

const requestPasswordReset = async (email: string) => {
    return await api.post("/password/email", { email });
}

type AuthTokenPayload = {
    access_token?: string;
    refresh_token?: string;
    data?: { access_token?: string; refresh_token?: string };
};

function extractAuthTokens(body: unknown): {
    access?: string;
    refresh?: string;
} {
    if (!body || typeof body !== "object") return {};
    const d = body as AuthTokenPayload;
    const nested = d.data;
    return {
        access:
            (typeof d.access_token === "string" && d.access_token) ||
            (nested &&
                typeof nested.access_token === "string" &&
                nested.access_token) ||
            undefined,
        refresh:
            (typeof d.refresh_token === "string" && d.refresh_token) ||
            (nested &&
                typeof nested.refresh_token === "string" &&
                nested.refresh_token) ||
            undefined,
    };
}

const resetPassword = async (
    email: string,
    password: string,
    passwordConfirmation: string,
    token: string,
) => {
    const resp = await api.post("/reset-password", {
        email,
        password,
        password_confirmation: passwordConfirmation,
        token,
    });
    const { access, refresh } = extractAuthTokens(resp.data);
    if (access) {
        api.applyAuthTokens(access, refresh, true);
    }
    return resp;
};

export default {
    login,
    logout,
    register,
    getUser,
    resendEmailVerification,
    verifyEmail: (id: string, hash: string) => api.get(`/email/verify/${id}/${hash}`),
    clearAccessToken: api.clearAccessToken,
    getAccessToken: api.getAccessToken,
    getKeepUserLoggedIn: api.getKeepUserLoggedIn,
    markQuestionnaireSubmit,
    requestPasswordReset,
    resetPassword,
    getPatientInvitationPrefill,
};
